import { QuestType, RewardType } from "src/hooks/affiliate";

/**
 * A preset quest template — drop-in values for the "เพิ่ม Quest" form.
 * The user picks one from a dropdown, the form auto-fills, then they tweak
 * (campaign code, dates, reward value, etc.) and submit.
 *
 * Reference: pigslot-service/affiliate/.claude/quest_types.md
 *   - Backend reads `condition.metric_type` and matches against `QuestType.*.value`
 *   - Validation hint #4 in doc: condition.target_value มักเท่ากับ quest.target_value
 *     → if `conditionTargetValue` is omitted, applyTemplate falls back to `targetValue`
 *   - For provider-filtered types: extra_config.game_provider matches
 *     BetTransactionSchema.game_provider (alias ref_type). product_type is optional
 *     and filters BetTransactionSchema.product_type (PIG_SPIN / PIG_CASINO / PIG_SPORT).
 */
/**
 * Bonus (coin-wallet) preset for a BONUS-reward quest. Mirrors the Bonus
 * sub-form fields in AddQuestModal. `durationInHours` = วันหมดอายุ × 24,
 * `turnoverRatio` = เทิร์น (เช่น 5 = เทิร์น 5 เท่า).
 */
export interface QuestTemplateBonus {
  description?: string;
  initialCoinAmount?: number;
  bonusRatio?: number;
  turnoverRatio?: number;
  durationInHours?: number;
  maximumBonus?: number;
  maximumPerDay?: number;
  convertLimitAmount?: number;
  convertLimitRatio?: number;
}

export interface QuestTemplate {
  id: string;
  name: string;
  description: string;
  /** Duration in days from today used to default the dateRange picker. */
  defaultDurationDays: number;
  questName: string;
  questType: QuestType;
  targetValue: number;
  rewardType: RewardType;
  rewardValue?: number;
  /** Defaults to `targetValue` when omitted (per backend validation hint). */
  conditionTargetValue?: number;
  /** Important for REFERRAL_TURNOVER_CONTINUE to avoid duplicate counting. */
  conditionIsUniqueUser?: boolean;
  /** Stored as a JSON object; serialised into the textarea on apply. */
  extraConfig?: Record<string, unknown>;
  /** Auto-filled into the Bonuses sub-form. ใช้กับ reward_type = BONUS. */
  bonuses?: QuestTemplateBonus[];
}

export const QUEST_TEMPLATES: QuestTemplate[] = [
  // ── แคมเปญแนะนำ (ชุดมาตรฐาน) ───────────────────────────────────────
  // โบนัส: initialCoinAmount = ยอดโบนัส, turnoverRatio = เทิร์น, durationInHours = วันหมดอายุ × 24
  {
    id: "preset_pupuen",
    name: "ปูพื้น — ชวนฝากครั้งแรก 5 คน",
    description:
      "ชวน downline ฝากเงินครั้งแรกได้ 5 คน → โบนัส 500 บาท (เทิร์น 5 เท่า / หมดอายุ 14 วัน). ภายใน 30 วันแรก",
    defaultDurationDays: 30,
    questName: "ปูพื้น — ชวนฝากครั้งแรก 5 คน",
    questType: QuestType.REFERRAL_COUNT,
    targetValue: 5,
    rewardType: RewardType.BONUS,
    rewardValue: 0,
    bonuses: [
      {
        description: "โบนัสปูพื้น",
        initialCoinAmount: 500,
        bonusRatio: 1,
        turnoverRatio: 5,
        durationInHours: 14 * 24,
      },
    ],
  },
  {
    id: "preset_saen_raek",
    name: "แสนแรก — turnover รวมถึง 100,000",
    description:
      "ยอดเดิมพันรวมของ downline ในเครือถึง 100,000 บาท → โบนัส 1,000 บาท (เทิร์น 5 เท่า / หมดอายุ 14 วัน). ทำซ้ำได้ทุก 30 วัน",
    defaultDurationDays: 30,
    questName: "แสนแรก — turnover รวมถึง 100,000",
    questType: QuestType.REFERRAL_TURNOVER,
    // target_value = ยอด turnover (บาท)
    targetValue: 100000,
    rewardType: RewardType.BONUS,
    rewardValue: 0,
    bonuses: [
      {
        description: "โบนัสแสนแรก",
        initialCoinAmount: 1000,
        bonusRatio: 1,
        turnoverRatio: 5,
        durationInHours: 14 * 24,
      },
    ],
  },
  {
    id: "preset_network_diamond",
    name: "เน็ตเวิร์คเพชร — 3 คน ฝากซ้ำ ≥ 3 ครั้ง",
    description:
      "downline ที่ฝากซ้ำตั้งแต่ 3 ครั้งขึ้นไป จำนวน 3 คน → เพิ่มค่าคอมมิชชัน +5% (ไม่มีเทิร์น). ตลอดแคมเปญ",
    defaultDurationDays: 90,
    questName: "เน็ตเวิร์คเพชร — 3 คน ฝากซ้ำ ≥ 3 ครั้ง",
    questType: QuestType.REFERRAL_STACK,
    targetValue: 3,
    rewardType: RewardType.COMMISSION_PERCENTAGE,
    rewardValue: 5,
    extraConfig: { min_per_user: 3 },
  },
  {
    id: "preset_strong_team",
    name: "ทีมแกร่ง — 15 วันต่อเนื่อง วันละ ≥ 5 คน",
    description:
      "downline เล่นเดิมพันต่อเนื่อง 15 วัน โดยแต่ละวันต้องมี downline ≥5 คนเล่น (≥100 บาท/ตา) → โบนัส 1,000 บาท (เทิร์น 5 เท่า / หมดอายุ 14 วัน). ทำซ้ำได้ทุก 30 วัน",
    defaultDurationDays: 30,
    questName: "ทีมแกร่ง — 15 วันต่อเนื่อง วันละ ≥ 5 คน",
    questType: QuestType.AFFILIATE_INVITE_CONSECUTIVE_PLAYERS,
    // target_value = จำนวนวันต่อเนื่อง (ไม่ใช่จำนวน downline)
    targetValue: 15,
    rewardType: RewardType.BONUS,
    rewardValue: 0,
    extraConfig: { min_bet_amount: 100, min_downlines_per_day: 5 },
    bonuses: [
      {
        description: "โบนัสทีมแกร่ง",
        initialCoinAmount: 1000,
        bonusRatio: 1,
        turnoverRatio: 5,
        durationInHours: 14 * 24,
      },
    ],
  },
  {
    id: "preset_streak_7d_pigslot",
    name: "เล่นต่อเนื่อง 7 วันในเดือนนั้น (PIGSLOT)",
    description:
      "ชวนมาเล่นต่อเนื่อง 7 วันติดในเดือนนั้น → โบนัส 500 บาท (เล่นได้เฉพาะ PIGSLOT / เทิร์น 5 เท่า / หมดอายุ 14 วัน)",
    defaultDurationDays: 30,
    questName: "เล่นต่อเนื่อง 7 วันในเดือนนั้น (PIGSLOT)",
    questType: QuestType.AFFILIATE_INVITE_CONSECUTIVE_PLAYERS,
    // target_value = จำนวนวันต่อเนื่อง
    targetValue: 7,
    rewardType: RewardType.BONUS,
    rewardValue: 0,
    extraConfig: { min_bet_amount: 100, min_downlines_per_day: 1 },
    bonuses: [
      {
        // หมายเหตุ: การจำกัดให้เล่นได้เฉพาะ PIGSLOT เป็น config ฝั่ง coin wallet
        // ไม่มีช่องในฟอร์ม bonus — ระบุไว้ใน description ให้ผู้ดูแลตั้งค่าเพิ่ม
        description: "โบนัสเล่นได้เฉพาะ PIGSLOT",
        initialCoinAmount: 500,
        bonusRatio: 1,
        turnoverRatio: 5,
        durationInHours: 14 * 24,
      },
    ],
  },
  {
    id: "preset_comeback",
    name: "ดึงกลับมา — 5 คนที่หายไป 30 วัน",
    description:
      "ดึง downline ที่หายไป ≥30 วันให้กลับมาเล่น → 300 บาท/คน เงินสด ถอนได้เลย (สูงสุด 5 คน). ภายใน 60 วัน",
    defaultDurationDays: 60,
    questName: "ดึงกลับมา — 5 คนที่หายไป 30 วัน",
    questType: QuestType.REFERRAL_INVITE_COMEBACK_PLAYERS,
    targetValue: 5,
    rewardType: RewardType.FIXED_MONEY,
    rewardValue: 300,
    extraConfig: { inactive_days: 30 },
  },
  {
    id: "preset_million",
    name: "หลักล้าน — turnover สะสม 1,000,000",
    description:
      "ยอดเดิมพันสะสมรวมถึง 1,000,000 บาท → 5,000 บาท เงินสด ถอนได้เลย. ไม่จำกัดเวลา",
    defaultDurationDays: 365,
    questName: "หลักล้าน — turnover สะสม 1,000,000",
    questType: QuestType.REFERRAL_TURNOVER,
    targetValue: 1000000,
    rewardType: RewardType.FIXED_MONEY,
    rewardValue: 5000,
  },
  {
    id: "preset_five_million",
    name: "ห้าล้านเดือด — turnover สะสม 5,000,000",
    description:
      "ยอดเดิมพันสะสมรวมถึง 5,000,000 บาท → 20,000 บาท เงินสด ถอนได้เลย (ไม่มีเทิร์น). ไม่จำกัดเวลา",
    defaultDurationDays: 365,
    questName: "ห้าล้านเดือด — turnover สะสม 5,000,000",
    questType: QuestType.REFERRAL_TURNOVER,
    targetValue: 5000000,
    rewardType: RewardType.FIXED_MONEY,
    rewardValue: 20000,
  },

  // ── Referral count ────────────────────────────────────────────────
  {
    id: "referral_count_5",
    name: "ชวนเพื่อนฝาก 5 คน",
    description: "ชวน downline ใหม่ 5 คนแรกที่ฝากเงิน → รับเงินคืน 100 บาท",
    defaultDurationDays: 30,
    questName: "ชวนเพื่อนฝาก 5 คน",
    questType: QuestType.REFERRAL_COUNT,
    targetValue: 5,
    rewardType: RewardType.FIXED_MONEY,
    rewardValue: 100,
  },
  {
    id: "referral_stack_3_x_2",
    name: "downline 3 คน ฝาก ≥ 2 ครั้ง",
    description: "downline ที่ฝาก 2 ครั้งขึ้นไป — รวม 3 คน → รับเงินคืน 200 บาท",
    defaultDurationDays: 30,
    questName: "ลูกค้าประจำ 3 คน (ฝาก ≥ 2 ครั้ง)",
    questType: QuestType.REFERRAL_STACK,
    targetValue: 3,
    rewardType: RewardType.FIXED_MONEY,
    rewardValue: 200,
    extraConfig: { min_per_user: 2 },
  },

  // ── Referral activity ─────────────────────────────────────────────
  {
    id: "referral_turnover_50k",
    name: "turnover รวมครบ 50,000 บาท",
    description: "downline ทุกคนในเครือทำยอด turnover รวมสะสมครบ 50,000 บาท → รับ commission 5%",
    defaultDurationDays: 30,
    questName: "Turnover รวมครบ 50,000",
    questType: QuestType.REFERRAL_TURNOVER,
    // target_value = ยอด turnover (บาท) — ไม่ใช่จำนวน downline/step
    targetValue: 50000,
    rewardType: RewardType.COMMISSION_PERCENTAGE,
    rewardValue: 5,
  },
  {
    id: "referral_turnover_200k",
    name: "turnover รวมครบ 200,000 บาท",
    description: "downline ทุกคนในเครือทำยอด turnover รวมสะสมครบ 200,000 บาท → รับเงินคืน 1,000 บาท",
    defaultDurationDays: 30,
    questName: "Turnover รวมครบ 200,000",
    questType: QuestType.REFERRAL_TURNOVER,
    targetValue: 200000,
    rewardType: RewardType.FIXED_MONEY,
    rewardValue: 1000,
  },
  {
    id: "referral_turnover_continue_5_x_2d",
    name: "downline 5 คน เล่นต่อ 2 วัน",
    description: "downline 5 คน เล่นเดิมพันติดต่อกัน 2 วัน → Bonus (unique per downline)",
    defaultDurationDays: 14,
    questName: "downline 5 คน เล่นต่อเนื่อง 2 วัน",
    questType: QuestType.REFERRAL_TURNOVER_CONTINUE,
    targetValue: 5,
    rewardType: RewardType.BONUS,
    rewardValue: 0,
    conditionIsUniqueUser: true,
    extraConfig: { min_streak: 2 },
  },
  {
    id: "referral_turnover_continue_5_x_7d",
    name: "downline 5 คน เล่นต่อ 7 วัน",
    description: "downline 5 คน เล่นเดิมพันติดต่อกัน 7 วัน → Bonus ใหญ่ (unique per downline)",
    defaultDurationDays: 30,
    questName: "downline 5 คน เล่นต่อเนื่อง 7 วัน",
    questType: QuestType.REFERRAL_TURNOVER_CONTINUE,
    targetValue: 5,
    rewardType: RewardType.BONUS,
    rewardValue: 0,
    conditionIsUniqueUser: true,
    extraConfig: { min_streak: 7 },
  },

  // ── Provider-specific ─────────────────────────────────────────────
  // NB: extra_config.game_provider matches BetTransactionSchema.game_provider (alias ref_type).
  // extra_config.product_type is optional and matches BetTransactionSchema.product_type
  // — values: PIG_SPIN / PIG_CASINO / PIG_SPORT.
  {
    id: "referral_invite_pg_slot_5",
    name: "ชวน 5 คน เล่น PG Slot (≥100/bet)",
    description: "ชวน downline 5 คน เล่นสล็อต PG Soft ขั้นต่ำ 100 บาท/bet → รับเงินคืน 100 บาท",
    defaultDurationDays: 30,
    questName: "ชวน 5 คน เล่น PG Slot",
    questType: QuestType.REFERRAL_INVITE_PLAYERS,
    targetValue: 5,
    rewardType: RewardType.FIXED_MONEY,
    rewardValue: 100,
    extraConfig: {
      game_provider: "PG_GAME",
      product_type: "PIG_SPIN",
      min_bet_amount: 100,
    },
  },
  {
    id: "referral_invite_pragmatic_slot_5",
    name: "ชวน 5 คน เล่น Pragmatic Slot (≥100/bet)",
    description: "ชวน downline 5 คน เล่นสล็อต Pragmatic Play ขั้นต่ำ 100 บาท/bet",
    defaultDurationDays: 30,
    questName: "ชวน 5 คน เล่น Pragmatic Slot",
    questType: QuestType.REFERRAL_INVITE_PLAYERS,
    targetValue: 5,
    rewardType: RewardType.FIXED_MONEY,
    rewardValue: 100,
    extraConfig: {
      game_provider: "PRAGMATIC_SLOT_GAME",
      product_type: "PIG_SPIN",
      min_bet_amount: 100,
    },
  },
  {
    id: "referral_invite_pragmatic_casino_5",
    name: "ชวน 5 คน เล่น Pragmatic Casino (≥200/bet)",
    description: "ชวน downline 5 คน เล่นคาสิโนสด Pragmatic Play ขั้นต่ำ 200 บาท/bet",
    defaultDurationDays: 30,
    questName: "ชวน 5 คน เล่น Pragmatic Casino",
    questType: QuestType.REFERRAL_INVITE_PLAYERS,
    targetValue: 5,
    rewardType: RewardType.FIXED_MONEY,
    rewardValue: 200,
    extraConfig: {
      game_provider: "PRAGMATIC_LIVE_GAME",
      product_type: "PIG_CASINO",
      min_bet_amount: 200,
    },
  },
  {
    id: "referral_invite_provider_active_pg_slot_30d",
    name: "5 คน เล่น PG Slot ครบ 30 วัน",
    description: "downline 5 คน เล่นสล็อต PG Soft ครบ 30 วัน (distinct days), ≥100 บาท/bet",
    defaultDurationDays: 60,
    questName: "downline 5 คน เล่น PG Slot ครบ 30 วัน",
    questType: QuestType.REFERRAL_INVITE_PROVIDER_ACTIVE_DURATION,
    targetValue: 5,
    rewardType: RewardType.BONUS,
    rewardValue: 0,
    extraConfig: {
      game_provider: "PG_GAME",
      product_type: "PIG_SPIN",
      min_active_days: 30,
      min_bet_amount: 100,
    },
  },
  {
    id: "referral_invite_provider_active_specific_game_14d",
    name: "5 คน เล่น Sweet Bonanza ครบ 14 วัน",
    description: "downline 5 คน เล่นเกม Sweet Bonanza (Pragmatic SLOT) ครบ 14 วัน, ≥50 บาท/bet",
    defaultDurationDays: 30,
    questName: "downline 5 คน เล่น Sweet Bonanza ครบ 14 วัน",
    questType: QuestType.REFERRAL_INVITE_PROVIDER_ACTIVE_DURATION,
    targetValue: 5,
    rewardType: RewardType.BONUS,
    rewardValue: 0,
    extraConfig: {
      game_provider: "PRAGMATIC_SLOT_GAME",
      product_type: "PIG_SPIN",
      game_id: "PP_sweet_bonanza",
      min_active_days: 14,
      min_bet_amount: 50,
    },
  },
  {
    id: "referral_invite_provider_active_pragmatic_casino_14d",
    name: "5 คน เล่น Pragmatic Casino ครบ 14 วัน",
    description: "downline 5 คน เล่นคาสิโนสด Pragmatic Play ครบ 14 วัน (distinct days), ≥200 บาท/bet",
    defaultDurationDays: 30,
    questName: "downline 5 คน เล่น Pragmatic Casino ครบ 14 วัน",
    questType: QuestType.REFERRAL_INVITE_PROVIDER_ACTIVE_DURATION,
    targetValue: 5,
    rewardType: RewardType.BONUS,
    rewardValue: 0,
    extraConfig: {
      game_provider: "PRAGMATIC_LIVE_GAME",
      product_type: "PIG_CASINO",
      min_active_days: 14,
      min_bet_amount: 200,
    },
  },

  // ── Consecutive (upline-level streak) ─────────────────────────────
  // min_downlines_per_day = จำนวน downline ขั้นต่ำที่ต้องเล่นในวันนั้นจึงนับเป็น active day
  {
    id: "affiliate_consecutive_7d",
    name: "downline เล่นต่อเนื่อง 7 วัน",
    description: "วันที่มี downline ≥1 คนเล่น ≥100 บาท → +1 streak; พลาด 1 วันรีเซ็ต. ครบ 7 วัน",
    defaultDurationDays: 30,
    questName: "downline เล่นต่อเนื่อง 7 วัน",
    questType: QuestType.AFFILIATE_INVITE_CONSECUTIVE_PLAYERS,
    targetValue: 7,
    rewardType: RewardType.BONUS,
    rewardValue: 0,
    extraConfig: { min_bet_amount: 100, min_downlines_per_day: 1 },
  },
  {
    id: "affiliate_consecutive_7d_3players",
    name: "7 วันต่อเนื่อง วันละ ≥ 3 คน",
    description: "ทุกวันต้องมี downline ≥3 คนเล่น ≥100 บาทจึงนับ; ต่อเนื่องครบ 7 วัน → Bonus",
    defaultDurationDays: 30,
    questName: "7 วันต่อเนื่อง วันละ ≥ 3 คน",
    questType: QuestType.AFFILIATE_INVITE_CONSECUTIVE_PLAYERS,
    targetValue: 7,
    rewardType: RewardType.BONUS,
    rewardValue: 0,
    extraConfig: { min_bet_amount: 100, min_downlines_per_day: 3 },
  },
  {
    id: "affiliate_consecutive_14d",
    name: "downline เล่นต่อเนื่อง 14 วัน",
    description: "downline (รวมกัน) เล่นต่อเนื่อง 14 วันโดยไม่ขาด → Bonus ใหญ่",
    defaultDurationDays: 45,
    questName: "downline เล่นต่อเนื่อง 14 วัน",
    questType: QuestType.AFFILIATE_INVITE_CONSECUTIVE_PLAYERS,
    targetValue: 14,
    rewardType: RewardType.BONUS,
    rewardValue: 0,
    extraConfig: { min_bet_amount: 100, min_downlines_per_day: 1 },
  },

  // ── Turnover within a provider / game ─────────────────────────────
  {
    id: "referral_turnover_provider_pragmatic_10k",
    name: "5 คน turnover Pragmatic Play ครบ 10,000",
    description: "downline 5 คน ทำ turnover สะสมในค่าย Pragmatic Play ครบ 10,000 บาท → รับเงินคืน 200 บาท",
    defaultDurationDays: 30,
    questName: "5 คน turnover Pragmatic Play ครบ 10,000",
    questType: QuestType.REFERRAL_TURNOVER_GAME_PROVIDERS,
    targetValue: 5,
    rewardType: RewardType.FIXED_MONEY,
    rewardValue: 200,
    extraConfig: {
      game_provider: "PRAGMATIC_SLOT_GAME",
      min_turnover: 10000,
    },
  },
  {
    id: "referral_turnover_game_sweet_bonanza_5k",
    name: "5 คน turnover Sweet Bonanza ครบ 5,000",
    description: "downline 5 คน ทำ turnover สะสมในเกม Sweet Bonanza ครบ 5,000 บาท → Bonus",
    defaultDurationDays: 30,
    questName: "5 คน turnover Sweet Bonanza ครบ 5,000",
    questType: QuestType.REFERRAL_TURNOVER_GAME_PROVIDERS,
    targetValue: 5,
    rewardType: RewardType.BONUS,
    rewardValue: 0,
    extraConfig: {
      game_provider: "PRAGMATIC_SLOT_GAME",
      game_id: "PP_sweet_bonanza",
      min_turnover: 5000,
    },
  },

  // ── Distinct games within a provider ──────────────────────────────
  {
    id: "referral_invite_distinct_pragmatic_3games",
    name: "5 คน เล่น Pragmatic Play ≥ 3 เกมต่างกัน",
    description: "downline 5 คน เล่นเกมใน Pragmatic Play อย่างน้อย 3 เกมต่างกัน, ≥100 บาท/bet → รับเงินคืน 150 บาท",
    defaultDurationDays: 30,
    questName: "5 คน เล่น Pragmatic Play ≥ 3 เกมต่างกัน",
    questType: QuestType.REFERRAL_INVITE_DISTINCT_GAME_PROVIDERS,
    targetValue: 5,
    rewardType: RewardType.FIXED_MONEY,
    rewardValue: 150,
    extraConfig: {
      game_provider: "PRAGMATIC_SLOT_GAME",
      min_distinct_games: 3,
      min_bet_amount: 100,
    },
  },

  // ── Comeback (re-activate lapsed downlines) ───────────────────────
  {
    id: "referral_invite_comeback_30d",
    name: "ดึง 5 คนที่หายไป 30 วันกลับมาเล่น",
    description: "ดึง downline 5 คนที่หายไป ≥30 วันให้กลับมาเล่น ≥100 บาท/bet → รับเงินคืน 100 บาท",
    defaultDurationDays: 30,
    questName: "ดึง 5 คนที่หายไป 30 วันกลับมาเล่น",
    questType: QuestType.REFERRAL_INVITE_COMEBACK_PLAYERS,
    targetValue: 5,
    rewardType: RewardType.FIXED_MONEY,
    rewardValue: 100,
    extraConfig: {
      inactive_days: 30,
      min_bet_amount: 100,
    },
  },
  {
    id: "referral_invite_comeback_60d_pragmatic",
    name: "ดึง 3 คนที่หายไป 60 วันกลับมาเล่น Pragmatic",
    description: "ดึง downline 3 คนที่หายไป ≥60 วันให้กลับมาเล่นค่าย Pragmatic Play ≥50 บาท/bet → Bonus",
    defaultDurationDays: 45,
    questName: "ดึง 3 คนที่หายไป 60 วันกลับมาเล่น Pragmatic",
    questType: QuestType.REFERRAL_INVITE_COMEBACK_PLAYERS,
    targetValue: 3,
    rewardType: RewardType.BONUS,
    rewardValue: 0,
    extraConfig: {
      inactive_days: 60,
      game_provider: "PRAGMATIC_SLOT_GAME",
      min_bet_amount: 50,
    },
  },
];

export const findQuestTemplate = (id: string): QuestTemplate | undefined =>
  QUEST_TEMPLATES.find((t) => t.id === id);
